import type { ComputedRef, Ref } from 'vue';
import type { RouteLocationNamedRaw } from 'vue-router';
import {
  computed,
  toRef,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';

import type { VRouterTab } from '@components/tabs/VRouterTabs/types';
import type { IChangeHistoryEntry } from '@modules/change-history/components/VChangeHistory/types';
import type {
  PlayerDetailsProps,
  PlayerMutationPayload,
  PlayerTrackingHistoryFormData,
} from '@modules/players/submodules/PlayerProfile/types';
import { useI18n } from '@modules/i18n/composables';
import { useMerchantIdStore } from '@modules/merchant/store';
import { useNamespaceRoutes } from '@modules/permissions/composables';
import { usePermissionsStore } from '@modules/permissions/store';
import { PLAYERS_PERMISSIONS } from '@modules/players/constants';
import {
  TRANSLATION_PLAYER_DETAILS,
} from '@modules/players/submodules/PlayerProfile/constants';
import {
  useTrackingHistoryCreateMutation,
  useTrackingHistoryRemoveMutation,
} from '@modules/players/submodules/PlayerProfile/mutations';
import { mapListToColumns } from '@modules/players/submodules/PlayerProfile/pages/PlayerProfilePage/composable/utils';
import {
  usePlayerProfileQuery,
  usePlayerTrackingHistoriesQuery,
} from '@modules/players/submodules/PlayerProfile/queries';
import { RESPONSE_DATE_FORMAT } from '@modules/smart-filters/constants';
import { formatDate } from '@vueuse/core';

interface PlayerProfilePageComposable {
  tabItems: ComputedRef<VRouterTab[]>;
  isTrackingPage: ComputedRef<boolean>;
  componentProps: Ref<PlayerDetailsProps>;
  playerHistoryList: Ref<IChangeHistoryEntry[] | undefined>;
  onSubmit(payload: PlayerTrackingHistoryFormData): void;
  onRemove(history: IChangeHistoryEntry): Promise<void>;
  isPlayerProfileLoading: Ref<boolean>;
  toEditPage(data: IChangeHistoryEntry): Promise<void>;
  clearError(key: string): void;
}

export default function usePlayerProfilePage(
  mainDataRoute: RouteLocationNamedRaw,
  activityRoute: RouteLocationNamedRaw,
  trackerRoute: RouteLocationNamedRaw,
  editHistoryRoute: RouteLocationNamedRaw,
): PlayerProfilePageComposable {
  const route = useRoute();
  const router = useRouter();
  const { getTranslation } = useI18n();
  const { hasPermission } = usePermissionsStore();
  const merchantIdFromStore = toRef(useMerchantIdStore(), 'merchantId');
  const { isCurrentRouteChildOf } = useNamespaceRoutes();

  const playerId = computed<string>(() => (route.params.id ? String(route.params.id) : ''));
  const merchant = computed<number>(() => (route.params.merchant ? Number(route.params.merchant) : 0));
  const historyId = computed<number>(() => (route.params.historyId ? Number(route.params.historyId) : 0));
  const merchantId = computed<number>(() => merchant.value || merchantIdFromStore.value);

  const {
    data: playerDataResponse,
    isLoading: isPlayerProfileLoading,
  } = usePlayerProfileQuery(playerId, merchantId);

  const {
    data: playerHistories,
  } = usePlayerTrackingHistoriesQuery(playerId, merchantId);

  const {
    mutate: createHistory,
    errors,
    clearError,
    isLoading: isHistoryMutateLoading,
  } = useTrackingHistoryCreateMutation();

  const {
    mutateAsync: removeHistory,
  } = useTrackingHistoryRemoveMutation();

  const tabItems = computed<VRouterTab[]>(() => [
    {
      label: getTranslation(TRANSLATION_PLAYER_DETAILS, 'MAIN_DATA_TAB_NAME').value,
      route: mainDataRoute,
      visible: hasPermission(PLAYERS_PERMISSIONS.PLAYERS_SHOW).value,
    },
    {
      label: getTranslation(TRANSLATION_PLAYER_DETAILS, 'ACTIVITY_TAB_NAME').value,
      route: activityRoute,
      visible: hasPermission(PLAYERS_PERMISSIONS.PLAYERS_SHOW).value,
    },
    {
      label: getTranslation(TRANSLATION_PLAYER_DETAILS, 'TRACKER_NAME_TAB_NAME').value,
      route: trackerRoute,
      visible: hasPermission(PLAYERS_PERMISSIONS.PLAYERS_SHOW).value,
    },
  ]);

  const isTrackingPage = computed(() => {
    try {
      return isCurrentRouteChildOf(trackerRoute);
    } catch {
      return false;
    }
  });

  const playerData = computed(() => playerDataResponse.value?.player);

  const componentProps = computed<PlayerDetailsProps>(() => {
    let playerDetailProps: PlayerDetailsProps = {
      playerData: playerData.value,
      formProfileColumns: mapListToColumns(
        route.name,
        playerDataResponse.value?.columns_view_order,
        mainDataRoute.name,
        activityRoute.name,
        trackerRoute.name,
      ),
    };

    if (isTrackingPage.value) {
      playerDetailProps = {
        ...playerDetailProps,
        errors: errors.value,
        isLoading: isHistoryMutateLoading.value,
        clearError,
      };
    }

    return playerDetailProps;
  });

  const playerHistoryList = computed<IChangeHistoryEntry[] | undefined>(() => playerHistories.value?.map((item) => ({
    ...item,
    active: historyId.value === item.id,
  })));

  function onSubmit(payload: PlayerTrackingHistoryFormData): void {
    if (playerData.value) {
      const data: PlayerMutationPayload = {
        history_of_players_change: {
          ...playerData.value,
          ...payload,
          date: formatDate(payload.date, RESPONSE_DATE_FORMAT),
        },
      };
      createHistory({
        data,
        playerId: playerId.value,
        merchant: merchantId.value,
      });
    }
  }

  async function toEditPage(data: IChangeHistoryEntry): Promise<void> {
    await router.push({ name: editHistoryRoute.name, params: { historyId: data.id, merchant: merchantId.value } });
  }

  async function onRemove(history: IChangeHistoryEntry): Promise<void> {
    try {
      await removeHistory({
        playerId: playerId.value,
        merchant: merchantId.value,
        historyId: history.id,
      });

      if (historyId.value === history.id) {
        await router.replace(trackerRoute);
      }
    } catch {}
  }

  return {
    isTrackingPage,
    tabItems,
    componentProps,
    playerHistoryList,
    onSubmit,
    onRemove,
    isPlayerProfileLoading,
    toEditPage,
    clearError,
  };
}
